// Lightweight point-elevation lookup using the same open Terrarium DEM tiles
// as MapLibre terrain. This keeps radar physics independent of whether the user
// has enabled the visual terrain layer.

const TILE_SIZE = 256;
const LOOKUP_ZOOM = 12;
const MAX_MERCATOR_LAT = 85.05112878;

export function decodeTerrariumElevationM(red: number, green: number,
                                          blue: number): number {
  return red * 256 + green + blue / 256 - 32768;
}

export function terrariumTilePixel(lon: number, lat: number, zoom = LOOKUP_ZOOM):
    { x: number; y: number; px: number; py: number } {
  const n = 2 ** zoom;
  const wrappedLon = ((lon + 180) % 360 + 360) % 360 - 180;
  const clampedLat = Math.max(-MAX_MERCATOR_LAT, Math.min(MAX_MERCATOR_LAT, lat));
  const xFloat = (wrappedLon + 180) / 360 * n;
  const latRad = clampedLat * Math.PI / 180;
  const yFloat = (1 - Math.asinh(Math.tan(latRad)) / Math.PI) / 2 * n;
  const x = Math.min(n - 1, Math.max(0, Math.floor(xFloat)));
  const y = Math.min(n - 1, Math.max(0, Math.floor(yFloat)));
  return {
    x, y,
    px: Math.min(TILE_SIZE - 1, Math.max(0, Math.floor((xFloat - x) * TILE_SIZE))),
    py: Math.min(TILE_SIZE - 1, Math.max(0, Math.floor((yFloat - y) * TILE_SIZE))),
  };
}

/** Fetch one public DEM tile and decode the site elevation in kilometres MSL. */
export async function fetchSiteElevationKm(lon: number, lat: number): Promise<number> {
  const { x, y, px, py } = terrariumTilePixel(lon, lat);
  const response = await fetch(
    `https://elevation-tiles-prod.s3.amazonaws.com/terrarium/${LOOKUP_ZOOM}/${x}/${y}.png`);
  if (!response.ok) throw new Error(`terrain tile returned ${response.status}`);
  const bitmap = await createImageBitmap(await response.blob());
  try {
    const canvas = document.createElement('canvas');
    canvas.width = TILE_SIZE;
    canvas.height = TILE_SIZE;
    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context) throw new Error('2D canvas unavailable for terrain decoding');
    context.drawImage(bitmap, 0, 0);
    const [red, green, blue] = context.getImageData(px, py, 1, 1).data;
    return Math.max(0, decodeTerrariumElevationM(red!, green!, blue!) / 1000);
  } finally {
    bitmap.close();
  }
}
